import { FC, useEffect, useRef, useState } from 'react';
import './EventDescription.scss';
import clsx from 'clsx';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
} from '@mui/icons-material';
import EventUrlEditModal from '../EventUrlEditModal/EventUrlEditModal';
import { useTranslation } from 'react-i18next';

interface EventDescriptionProps {
  description: string;
  onChangeDescription: (description: string) => void;
  disabled?: boolean;
  /** Omit formatting toolbar (e.g. read-only temporary event preview). */
  hideToolbar?: boolean;
  className?: string;
}

const EventDescription: FC<EventDescriptionProps> = ({
  description,
  onChangeDescription,
  disabled = false,
  hideToolbar = false,
  className,
}) => {
  const { t } = useTranslation();
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const skipOnUpdateRef = useRef(true);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: t('calendar.eventMenu.addDescription'),
        emptyEditorClass: 'is-editor-empty',
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'event-description__link',
        },
      }),
    ],
    content: description || '',
    onUpdate: ({ editor, transaction }) => {
      if (disabled || skipOnUpdateRef.current || !transaction.docChanged) {
        return;
      }
      onChangeDescription(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'event-description__editor',
      },
      handleClick: (view, pos, event) => {
        const link = (event.target as HTMLElement).closest('a');
        if (link) {
          if (disabled) {
            return true;
          }
          event.preventDefault();
          // First set cursor to clicked position, then extend selection to entire link
          editor?.chain().focus().setTextSelection(pos).extendMarkRange('link').run();
          setIsLinkModalOpen(true);
          return true;
        }
        return false;
      },
      handleKeyDown: (_, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.stopPropagation();
          return false;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }
    skipOnUpdateRef.current = true;
    queueMicrotask(() => {
      skipOnUpdateRef.current = false;
    });
  }, [editor]);

  useEffect(() => {
    if (editor && description !== editor.getHTML()) {
      skipOnUpdateRef.current = true;
      editor.commands.setContent(description || '', false);
      queueMicrotask(() => {
        skipOnUpdateRef.current = false;
      });
    }
  }, [description, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  const handleLinkSubmit = (text: string, url: string) => {
    if (!editor) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const { from, to } = editor.state.selection;

      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent(text)
        .setTextSelection({ from, to: from + text.length })
        .setLink({ href: url })
        .run();
    }
    setIsLinkModalOpen(false);
  };

  const getSelectedText = () => {
    if (!editor) return '';
    const { from, to } = editor.state.selection;
    return editor.state.doc.textBetween(from, to, ' ');
  };

  const hasTextSelected = editor?.state.selection.content().content?.size > 0;

  return (
    <div className={clsx('event-description', className)}>
      <div
        className={clsx(
          'event-description__container',
          hideToolbar && 'event-description__container--no-toolbar'
        )}
      >
        {!hideToolbar && (
          <div className="event-description__toolbar">
            <button
              onClick={() => editor?.chain().focus().toggleBold().run()}
              className={editor?.isActive('bold') ? 'is-active' : ''}
              disabled={disabled}
              type="button"
              aria-label={t('calendar.eventMenu.bold')}
            >
              <FormatBold fontSize="small" />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              className={editor?.isActive('italic') ? 'is-active' : ''}
              disabled={disabled}
              type="button"
              aria-label={t('calendar.eventMenu.italic')}
            >
              <FormatItalic fontSize="small" />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              className={editor?.isActive('bulletList') ? 'is-active' : ''}
              disabled={disabled}
              type="button"
              aria-label={t('calendar.eventMenu.bulletedList')}
            >
              <FormatListBulleted fontSize="small" />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              className={editor?.isActive('orderedList') ? 'is-active' : ''}
              disabled={disabled}
              type="button"
              aria-label={t('calendar.eventMenu.numberedList')}
            >
              <FormatListNumbered fontSize="small" />
            </button>
            <button
              onClick={() => setIsLinkModalOpen(true)}
              className={clsx(
                editor?.isActive('link') && 'is-active',
                !hasTextSelected && 'is-disabled'
              )}
              disabled={disabled || !hasTextSelected}
              type="button"
              aria-label={t('calendar.eventMenu.link')}
            >
              <LinkIcon fontSize="small" />
            </button>
          </div>
        )}
        <EditorContent editor={editor} className="event-description__input" />
      </div>

      {!hideToolbar && (
        <EventUrlEditModal
          open={isLinkModalOpen}
          onClose={() => setIsLinkModalOpen(false)}
          onSubmit={handleLinkSubmit}
          initialUrl={editor?.getAttributes('link').href}
          initialText={getSelectedText()}
        />
      )}
    </div>
  );
};

export default EventDescription;
